import React, { useCallback, useEffect, useState } from 'react';
import { Search, AlertTriangle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

// =====================================================================
// FINANCEIRO › Controle de GPRS — A CONSULTA DA REDE.
//
// É o que a planilha no SharePoint NÃO dava: olhar as 14 lojas de uma vez. O
// escritório escolhe um período e vê os dias lançados, com as diferenças já
// calculadas pelo servidor, mais um resumo por loja.
//
// ⚠️ As diferenças vêm PRONTAS do `GET /consulta` — a tela não recalcula nada
//    aqui. Quem digita (a grade) recalcula ao vivo porque precisa; quem só lê,
//    não: duas implementações da mesma conta é como a planilha se perdeu.
//
// ⚠️ O gerente também entra nesta aba, e vê só a própria loja — o recorte é do
//    servidor (`recorteLoja`), não da tela.
// =====================================================================

const API = '/api/financeiro/gprs';

const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const brl = (n) =>
    n === null || n === undefined
        ? '—'
        : Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataBr = (iso) => String(iso || '').split('-').reverse().join('/');

const estiloInput = {
    padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8,
    fontSize: 14, color: '#0f172a', background: '#fff', fontFamily: 'inherit',
};

/** Vermelho quando não bate; cinza quando bate; neutro quando falta número. */
const corDiferenca = (v) => {
    if (v === null || v === undefined) return '#94a3b8';
    return Math.abs(v) < 0.005 ? '#64748b' : '#b91c1c';
};

/** Primeiro dia do mês corrente, em 'AAAA-MM-DD'. */
const inicioDoMes = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const hojeIso = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const GprsConsulta = () => {
    const [lojas, setLojas] = useState([]);
    const [filtros, setFiltros] = useState({ de: inicioDoMes(), ate: hojeIso(), loja: '' });
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/lojas`, { headers: auth() });
                if (r.ok) setLojas(await r.json());
            } catch { /* a lista de lojas é conveniência: sem ela o filtro some, a consulta segue */ }
        })();
    }, []);

    const buscar = useCallback(async () => {
        setCarregando(true);
        setErro('');
        try {
            const q = new URLSearchParams({ de: filtros.de, ate: filtros.ate });
            if (filtros.loja) q.set('loja', filtros.loja);
            const r = await fetch(`${API}/consulta?${q}`, { headers: auth() });
            const d = await r.json();
            if (!r.ok) throw new Error(d?.message || 'Erro ao consultar.');
            setDados(d);
        } catch (e) {
            setErro(e.message || 'Erro ao consultar.');
            setDados(null);
        } finally {
            setCarregando(false);
        }
    }, [filtros]);

    useEffect(() => { buscar(); /* só na abertura; depois é pelo botão */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * A EXPORTAÇÃO — planilha `.xlsx` de verdade, com as colunas de dinheiro em
     * FORMATO MOEDA (pedido da Julia, 16/09/2026).
     *
     * ⚠️⚠️ ERA CSV E NÃO PODIA CONTINUAR SENDO. **CSV é texto puro: ele não
     *    carrega formatação nenhuma** — o Excel abre tudo como "Geral", e a única
     *    forma de um CSV "parecer" moeda seria escrever `R$ 1.234,56` como TEXTO,
     *    que é pior: a coluna deixa de somar, de filtrar por valor e de entrar em
     *    tabela dinâmica. A planilha nativa resolve os dois de uma vez — o valor
     *    continua sendo NÚMERO e o formato é que mostra o R$.
     *
     * ⚠️ O `z` (formato de célula) funciona na versão community do SheetJS, que é
     *    a que o projeto usa; o que exige a versão paga é ESTILO (cor, fonte,
     *    borda). Por isso aqui há formato de número e não formatação visual.
     *
     * ⚠️ Exporta as MESMAS linhas exibidas, com as diferenças que vieram do
     *    servidor: recalcular na exportação criaria uma terceira versão da conta —
     *    o defeito que a planilha do SharePoint tinha.
     *
     * ⚠️ Célula EM BRANCO para o valor não preenchido, nunca 0: zero num relatório
     *    de conferência se lê como "declarou e não vendeu", e é justamente o dia
     *    em branco que alguém precisa enxergar para ir cobrar.
     */
    const exportarExcel = () => {
        if (!dados?.linhas?.length) return;

        // ⚠️ `null` (e não `''`) para o campo vazio: string vazia cria uma célula
        //    de TEXTO no meio de uma coluna numérica, e o Excel passa a reclamar
        //    de "números armazenados como texto" na coluna inteira.
        const num = (v) => (v === null || v === undefined ? null : Number(v));

        const linhas = dados.linhas.map((l) => ({
            'Loja': l.loja,
            'Data': dataBr(l.data),
            'Equipamentos': num(l.equipamento),
            'Fita POS': num(l.fitaPos),
            'Dif. máquina × Fita': num(l.maquinaVsFita),
            'Flex': num(l.flex),
            'Dif. Flex × Fita': num(l.flexVsFita),
            'Lançado por': l.por || '',
        }));

        const ws = XLSX.utils.json_to_sheet(linhas);

        // ⚠️ As colunas de dinheiro pelo ÍNDICE (C, D, E, F, G), não pelo nome:
        //    o `json_to_sheet` já fixou a ordem das chaves acima, e varrer por
        //    índice é o que mantém isto legível. Mudou a ordem lá em cima? Muda
        //    aqui — e o jeito de perceber é a coluna sair sem R$.
        const COLUNAS_MOEDA = [2, 3, 4, 5, 6];
        // `R$ #,##0.00` — o Excel traduz o separador conforme a região do
        // usuário, então em PT-BR isto aparece como `R$ 1.234,56`.
        const FORMATO_MOEDA = 'R$ #,##0.00';

        const intervalo = XLSX.utils.decode_range(ws['!ref']);
        // Começa na linha 1 (a 0 é o cabeçalho — formatá-lo faria o título virar número).
        for (let linha = 1; linha <= intervalo.e.r; linha++) {
            for (const coluna of COLUNAS_MOEDA) {
                const celula = ws[XLSX.utils.encode_cell({ r: linha, c: coluna })];
                // ⚠️ A guarda `t === 'n'` (número) é o que preserva o dia não
                //    preenchido: o valor nulo vira uma célula do tipo `'z'` (em
                //    branco), que o Excel mostra vazia. Formatá-la como moeda
                //    faria aparecer um `R$ 0,00` onde ninguém declarou nada — o
                //    mesmo engano que a tela evita pintando a diferença de cinza.
                if (celula && celula.t === 'n') celula.z = FORMATO_MOEDA;
            }
        }

        ws['!cols'] = [
            { wch: 8 },   // Loja
            { wch: 11 },  // Data
            { wch: 15 }, { wch: 15 }, { wch: 19 }, { wch: 15 }, { wch: 19 },  // dinheiro
            { wch: 28 },  // Lançado por
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Controle GPRS');

        // ⚠️ O nome do arquivo carrega o PERÍODO e a LOJA, para o arquivo não
        //    mentir depois de salvo no disco (mesma lição do parque de TI).
        const recorte = filtros.loja ? `_${filtros.loja}` : '';
        XLSX.writeFile(wb, `Controle_GPRS${recorte}_${filtros.de}_a_${filtros.ate}.xlsx`);
    };

    const th = {
        padding: '10px 12px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase',
        color: COR_FORTE, background: COR_FUNDO, borderBottom: `2px solid ${COR_BORDA}`,
        textAlign: 'right', whiteSpace: 'nowrap',
    };
    const td = {
        padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 14,
        textAlign: 'right', fontVariantNumeric: 'tabular-nums',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* ---------------- FILTROS ---------------- */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>De</span>
                    <input type="date" value={filtros.de} onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} style={estiloInput} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Até</span>
                    <input type="date" value={filtros.ate} onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} style={estiloInput} />
                </label>
                {lojas.length > 1 && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Loja</span>
                        <select value={filtros.loja} onChange={(e) => setFiltros({ ...filtros, loja: e.target.value })} style={{ ...estiloInput, width: 180 }}>
                            <option value="">Todas</option>
                            {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </label>
                )}
                <button
                    onClick={buscar}
                    disabled={carregando}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                        borderRadius: 8, border: 'none', fontWeight: 800, fontSize: 14, color: '#fff',
                        background: `linear-gradient(135deg, #16a39c 0%, ${COR} 100%)`, cursor: 'pointer',
                    }}
                >
                    <Search size={16} /> {carregando ? 'Buscando…' : 'Buscar'}
                </button>
                {!!dados?.linhas?.length && (
                    <button
                        onClick={exportarExcel}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                            borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff',
                            color: '#475569', fontWeight: 700, cursor: 'pointer', fontSize: 14,
                        }}
                    >
                        <Download size={16} /> Exportar Excel
                    </button>
                )}
            </div>

            {erro && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>
                    <AlertTriangle size={16} /> {erro}
                </div>
            )}

            {dados?.truncado && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 14, fontWeight: 600 }}>
                    <AlertTriangle size={16} /> A lista foi cortada em 2.000 linhas — estreite o período.
                </div>
            )}

            {/* ---------------- RESUMO POR LOJA ---------------- */}
            {!!dados?.resumo?.length && (
                <div>
                    <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 12px 0' }}>Resumo por loja</h3>
                    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...th, textAlign: 'left' }}>Loja</th>
                                    <th style={th}>Dias lançados</th>
                                    <th style={th}>Dias incompletos</th>
                                    <th style={th}>Dif. máquina × Fita</th>
                                    <th style={th}>Dif. Flex × Fita</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dados.resumo.map((r) => (
                                    <tr key={r.loja}>
                                        <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{r.loja}</td>
                                        <td style={td}>{r.dias}</td>
                                        <td style={{ ...td, color: r.diasIncompletos ? '#92400e' : '#64748b', fontWeight: r.diasIncompletos ? 700 : 400 }}>
                                            {r.diasIncompletos}
                                        </td>
                                        <td style={{ ...td, fontWeight: 700, color: corDiferenca(r.maquinaVsFita) }}>{brl(r.maquinaVsFita)}</td>
                                        <td style={{ ...td, fontWeight: 700, color: corDiferenca(r.flexVsFita) }}>{brl(r.flexVsFita)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0 0' }}>
                        A diferença da loja é a <strong>soma das diferenças diárias</strong> — os dias a que falta um número
                        entram na coluna “incompletos” e não na soma.
                    </p>
                </div>
            )}

            {/* ---------------- OS DIAS ---------------- */}
            <div>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', margin: '0 0 12px 0' }}>
                    Dias lançados {dados ? `(${dados.linhas.length})` : ''}
                </h3>
                {carregando ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Carregando…</div>
                ) : !dados?.linhas?.length ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Nenhum lançamento no período.</div>
                ) : (
                    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...th, textAlign: 'left' }}>Loja</th>
                                    <th style={{ ...th, textAlign: 'left' }}>Data</th>
                                    <th style={th}>Equipamentos</th>
                                    <th style={th}>Fita POS</th>
                                    <th style={th}>Dif. máquina × Fita</th>
                                    <th style={th}>Flex</th>
                                    <th style={th}>Dif. Flex × Fita</th>
                                    <th style={{ ...th, textAlign: 'left' }}>Lançado por</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dados.linhas.map((l) => (
                                    <tr key={`${l.loja}-${l.data}`}>
                                        <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{l.loja}</td>
                                        <td style={{ ...td, textAlign: 'left' }}>{dataBr(l.data)}</td>
                                        <td style={td}>{brl(l.equipamento)}</td>
                                        <td style={td}>{brl(l.fitaPos)}</td>
                                        <td style={{ ...td, fontWeight: 700, color: corDiferenca(l.maquinaVsFita) }}>{brl(l.maquinaVsFita)}</td>
                                        <td style={td}>{brl(l.flex)}</td>
                                        <td style={{ ...td, fontWeight: 700, color: corDiferenca(l.flexVsFita) }}>{brl(l.flexVsFita)}</td>
                                        <td style={{ ...td, textAlign: 'left', color: '#64748b', fontSize: 13 }}>{l.por || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GprsConsulta;
